import { parseUtcToLocalDate } from '@/utils/format'

function pad2 (value) {
  return String(value).padStart(2, '0')
}

function attendanceUtcMidnightToLocalDateKey (year, monthIndex, day) {
  const utcDate = new Date(Date.UTC(year, monthIndex, day, 0, 0, 0, 0))
  if (Number.isNaN(utcDate.getTime())) return ''
  return formatDateKey(
    utcDate.getFullYear(),
    utcDate.getMonth(),
    utcDate.getDate()
  )
}

function parseAttendanceUtcCalendarParts (value) {
  const str = String(value || '').trim()
  if (!str) return null

  const isoDate = str.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (isoDate) {
    return {
      year: Number(isoDate[1]),
      monthIndex: Number(isoDate[2]) - 1,
      day: Number(isoDate[3])
    }
  }

  const mdyDate = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})/)
  if (mdyDate) {
    return {
      year: Number(mdyDate[3]),
      monthIndex: Number(mdyDate[1]) - 1,
      day: Number(mdyDate[2])
    }
  }

  return null
}

export function formatMonthKey (year, monthIndex) {
  return `${year}-${pad2(monthIndex + 1)}`
}

export function formatDateKey (year, monthIndex, day) {
  return `${year}-${pad2(monthIndex + 1)}-${pad2(day)}`
}

export function buildCalendarCells (year, monthIndex) {
  const firstDay = new Date(year, monthIndex, 1).getDay()
  const daysInMonth = new Date(year, monthIndex + 1, 0).getDate()
  const cells = []

  for (let i = 0; i < firstDay; i += 1) {
    cells.push(null)
  }
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(day)
  }

  return cells
}

export function normalizeAttendanceDateKey (value) {
  if (value == null || value === '') return ''

  const parts = parseAttendanceUtcCalendarParts(value)
  if (parts) {
    const { year, monthIndex, day } = parts
    if (
      Number.isFinite(year) &&
      Number.isFinite(monthIndex) &&
      Number.isFinite(day) &&
      monthIndex >= 0 &&
      monthIndex <= 11 &&
      day >= 1 &&
      day <= 31
    ) {
      return attendanceUtcMidnightToLocalDateKey(year, monthIndex, day)
    }
  }

  const utcDate = parseUtcToLocalDate(value)
  if (!utcDate || Number.isNaN(utcDate.getTime())) return ''

  return formatDateKey(
    utcDate.getFullYear(),
    utcDate.getMonth(),
    utcDate.getDate()
  )
}

export function buildAttendedDateSet (dates) {
  const set = new Set()
  if (!Array.isArray(dates)) return set
  dates.forEach((item) => {
    const key = normalizeAttendanceDateKey(item)
    if (key) set.add(key)
  })
  return set
}

export function calcConsecutiveStreak (attendedSet, year, monthIndex, referenceDate = new Date()) {
  const refYear = referenceDate.getFullYear()
  const refMonth = referenceDate.getMonth()
  const refDay = referenceDate.getDate()
  const isSameMonth = refYear === year && refMonth === monthIndex

  let cursor
  if (isSameMonth) {
    const todayKey = formatDateKey(refYear, refMonth, refDay)
    cursor = new Date(refYear, refMonth, refDay)
    if (!attendedSet.has(todayKey)) {
      cursor.setDate(cursor.getDate() - 1)
    }
  } else {
    cursor = new Date(year, monthIndex + 1, 0)
  }

  let streak = 0
  while (cursor.getFullYear() === year && cursor.getMonth() === monthIndex) {
    const key = formatDateKey(cursor.getFullYear(), cursor.getMonth(), cursor.getDate())
    if (!attendedSet.has(key)) break
    streak += 1
    cursor.setDate(cursor.getDate() - 1)
  }

  return streak
}

export function formatAttendanceMonthLabel (year, monthIndex, locale) {
  const lang = String(locale || 'ko').toLowerCase()
  if (lang === 'ko') {
    return `${year}년 ${monthIndex + 1}월`
  }
  try {
    return new Intl.DateTimeFormat(lang, { year: 'numeric', month: 'long' }).format(
      new Date(year, monthIndex, 1)
    )
  } catch (err) {
    return `${year}-${pad2(monthIndex + 1)}`
  }
}
